import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight, CheckCircle, Menu, Clock } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Separator } from "@/components/ui/separator";

interface Module {
  id: string;
  title: string;
  description: string;
  order_number: number;
  video_duration: number;
  key_takeaways: string[];
  content: string | null;
  transcript: string | null;
}

interface Course {
  id: string;
  title: string;
  slug: string;
  total_modules: number;
}

const ModuleView = () => {
  const { slug, moduleNumber } = useParams<{ slug: string; moduleNumber: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [course, setCourse] = useState<Course | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [currentModule, setCurrentModule] = useState<Module | null>(null);
  const [completed, setCompleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    fetchCourseAndModules();
  }, [slug, moduleNumber]);

  const fetchCourseAndModules = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch course
      const { data: courseData, error: courseError } = await supabase
        .from("courses")
        .select("*")
        .eq("slug", slug)
        .single();

      if (courseError) throw courseError;
      setCourse(courseData);

      // Fetch all modules for this course
      const { data: modulesData, error: modulesError } = await supabase
        .from("course_modules")
        .select("*")
        .eq("course_id", courseData.id)
        .order("order_number");

      if (modulesError) throw modulesError;
      setModules(modulesData || []);

      // Find current module
      const current = modulesData?.find(m => m.order_number === parseInt(moduleNumber || "1"));
      setCurrentModule(current || null);

      // Check if current module is completed
      if (current) {
        const { data: progressData } = await supabase
          .from("user_course_progress")
          .select("completed")
          .eq("user_id", user.id)
          .eq("course_id", courseData.id)
          .eq("module_id", current.id)
          .single();

        setCompleted(progressData?.completed || false);
      }
    } catch (error: any) {
      toast({
        title: "Error loading module",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleModuleComplete = async (isCompleted: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user || !currentModule || !course) return;

      setCompleted(isCompleted);

      // Upsert progress
      const { error } = await supabase
        .from("user_course_progress")
        .upsert({
          user_id: user.id,
          course_id: course.id,
          module_id: currentModule.id,
          completed: isCompleted,
          completed_at: isCompleted ? new Date().toISOString() : null,
          updated_at: new Date().toISOString()
        }, {
          onConflict: "user_id,module_id"
        });

      if (error) throw error;

      if (isCompleted) {
        toast({
          title: "Module completed! 🎉",
          description: "Great progress! Keep going.",
        });

        // Check if this is the last module
        if (currentModule.order_number === course.total_modules) {
          toast({
            title: "Course completed! 🎊",
            description: `Congratulations! You've completed "${course.title}"`,
          });
        }
      }
    } catch (error: any) {
      toast({
        title: "Error updating progress",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const navigateToModule = (moduleNum: number) => {
    navigate(`/learning/course/${slug}/module/${moduleNum}`);
  };

  const getModuleStatus = (module: Module): "completed" | "current" | "upcoming" => {
    if (!currentModule) return "upcoming";
    if (module.id === currentModule.id) return "current";
    if (module.order_number < currentModule.order_number) return "completed";
    return "upcoming";
  };

  const calculateProgress = () => {
    const completedCount = modules.filter((_, idx) => 
      idx < (currentModule?.order_number || 1) - 1
    ).length + (completed ? 1 : 0);
    return Math.round((completedCount / modules.length) * 100);
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading module...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (!course || !currentModule) {
    return (
      <MainLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-4">Module not found</p>
          <Button onClick={() => navigate("/learning")}>Return to Learning Center</Button>
        </div>
      </MainLayout>
    );
  }

  const prevModule = modules.find(m => m.order_number === currentModule.order_number - 1);
  const nextModule = modules.find(m => m.order_number === currentModule.order_number + 1);

  return (
    <MainLayout>
      <div className="flex gap-6">
        {/* Sidebar */}
        {sidebarOpen && (
          <div className="w-80 flex-shrink-0">
            <Card className="sticky top-6">
              <CardHeader>
                <div className="flex items-center justify-between mb-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate("/learning")}
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Menu className="w-4 h-4" />
                  </Button>
                </div>
                <CardTitle className="text-lg">{course.title}</CardTitle>
                <div className="space-y-2 mt-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{calculateProgress()}%</span>
                  </div>
                  <Progress value={calculateProgress()} className="h-2" />
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="space-y-1 max-h-[60vh] overflow-y-auto">
                  {modules.map((module) => {
                    const status = getModuleStatus(module);
                    return (
                      <button
                        key={module.id}
                        onClick={() => navigateToModule(module.order_number)}
                        className={`w-full text-left p-3 rounded-lg transition-colors ${
                          status === "current"
                            ? "bg-primary text-primary-foreground"
                            : status === "completed"
                            ? "bg-accent/10 hover:bg-accent/20"
                            : "hover:bg-muted"
                        }`}
                      >
                        <div className="flex items-start gap-2">
                          {status === "completed" && (
                            <CheckCircle className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                          )}
                          {status === "current" && (
                            <div className="w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0" />
                          )}
                          {status === "upcoming" && (
                            <div className="w-4 h-4 rounded-full border-2 border-muted mt-0.5 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">
                              {module.order_number}. {module.title}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="space-y-6">
            {/* Breadcrumb */}
            <div className="text-sm text-muted-foreground">
              <button onClick={() => navigate("/learning")} className="hover:text-foreground">
                Learning
              </button>
              {" > "}
              <span className="text-foreground">{course.title}</span>
              {" > "}
              <span className="text-foreground">Module {currentModule.order_number}</span>
            </div>

            {!sidebarOpen && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-4 h-4 mr-2" />
                Show Modules
              </Button>
            )}

            {/* Module Header */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Badge variant="outline">Module {currentModule.order_number}</Badge>
              </div>
              <h1 className="text-3xl font-bold mb-2">{currentModule.title}</h1>
              <p className="text-muted-foreground">{currentModule.description}</p>
            </div>

            {/* Video Player Placeholder */}
            <Card>
              <CardContent className="p-0">
                <div className="aspect-video bg-muted flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                      <div className="w-0 h-0 border-l-8 border-l-primary border-t-6 border-t-transparent border-b-6 border-b-transparent ml-1"></div>
                    </div>
                    <p className="text-muted-foreground">Video player</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Content Tabs */}
            <Tabs defaultValue="takeaways">
              <TabsList>
                <TabsTrigger value="takeaways">Key Takeaways</TabsTrigger>
                <TabsTrigger value="transcript">Transcript</TabsTrigger>
                <TabsTrigger value="resources">Resources</TabsTrigger>
              </TabsList>

              <TabsContent value="takeaways" className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Key Takeaways</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {currentModule.key_takeaways?.map((takeaway, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="w-5 h-5 text-accent mt-0.5 flex-shrink-0" />
                          <span>{takeaway}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="transcript">
                <Card>
                  <CardHeader>
                    <CardTitle>Video Transcript</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      {currentModule.transcript || "Transcript coming soon..."}
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="resources">
                <Card>
                  <CardHeader>
                    <CardTitle>Additional Resources</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">
                      Related resources and materials will appear here.
                    </p>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Module Completion */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id="complete"
                      checked={completed}
                      onCheckedChange={handleModuleComplete}
                    />
                    <label
                      htmlFor="complete"
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      Mark module as complete
                    </label>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Navigation */}
            <Separator />
            <div className="flex items-center justify-between py-4">
              {prevModule ? (
                <Button
                  variant="outline"
                  onClick={() => navigateToModule(prevModule.order_number)}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous: {prevModule.title}
                </Button>
              ) : (
                <div />
              )}

              {nextModule ? (
                <Button
                  onClick={() => navigateToModule(nextModule.order_number)}
                >
                  Next: {nextModule.title}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={() => navigate("/learning")}>
                  Return to Learning Center
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default ModuleView;